import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-quill/dist/quill.snow.css";
import CampaignProfilesPage from "./page/CampaignProfilesPage";
import CardsPage from "./page/CardsPage";
import EditTemplatePage from "./page/EditTemplatePage";
import GoogleLogin from "./page/GoogleLogin";
import LoginPage from "./page/LoginPage";
import PhotoUploadPage from "./page/PhotoUploadPage";
import Users from "./page/Users";
import useAuthStore from "./store/useAuthStore";
import useThemeStore, {
  applyTheme,
  getSystemTheme,
  THEME_OPTIONS,
} from "./store/useThemeStore";

const mainNavItems = [
  { path: "/upload", label: "Scan", pageTitle: "Scan cards", icon: "scan" },
  { path: "/cards", label: "Cards", pageTitle: "Card library", icon: "cards" },
  {
    path: "/campaign-profiles",
    label: "Profiles",
    pageTitle: "Campaign profiles",
    icon: "profiles",
  },
  // Template management is intentionally hidden from navigation for now.
  // The route remains available so this can be restored without losing the page.
];

const Icon = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    scan: (
      <>
        <path d="M7 3H5a2 2 0 0 0-2 2v2M17 3h2a2 2 0 0 1 2 2v2M7 21H5a2 2 0 0 1-2-2v-2M17 21h2a2 2 0 0 0 2-2v-2" />
        <rect x="7" y="7" width="10" height="10" rx="2" />
      </>
    ),
    cards: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M7 10h4M7 14h7" />
      </>
    ),
    profiles: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 6h5M16 10h4M17 14h3" />
      </>
    ),
    templates: (
      <>
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M14 3v5h5M9 13h6M9 17h6" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20a6 6 0 0 1 12 0M16 4.5a3 3 0 0 1 0 5.8M17 14a5 5 0 0 1 4 4.9" />
      </>
    ),
    more: (
      <>
        <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
      </>
    ),
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </>
    ),
    moon: <path d="M20 15.5A8.5 8.5 0 0 1 8.5 4 8.5 8.5 0 1 0 20 15.5Z" />,
    monitor: (
      <>
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 22h8M12 18v4" />
      </>
    ),
    logout: (
      <>
        <path d="M10 5H5v14h5M14 8l4 4-4 4M8 12h10" />
      </>
    ),
    close: <path d="m6 6 12 12M18 6 6 18" />,
    chevron: <path d="m9 18 6-6-6-6" />,
    check: <path d="m5 12 4 4L19 6" />,
  };

  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

const ThemeButton = ({ resolvedTheme, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`}
    title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`}
    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-indigo-500 dark:hover:text-indigo-300"
  >
    <Icon name={resolvedTheme === "dark" ? "sun" : "moon"} className="h-[18px] w-[18px]" />
  </button>
);

const NavBar = ({ resolvedTheme }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useAuthStore();
  const clearToken = useAuthStore((state) => state.clearToken);
  const { theme, setTheme } = useThemeStore();
  const [moreOpen, setMoreOpen] = useState(false);

  const allNavItems =
    role === "admin"
      ? [
          ...mainNavItems,
          { path: "/users", label: "Users", pageTitle: "User access", icon: "users" },
        ]
      : mainNavItems;
  const mobilePrimaryItems = mainNavItems.slice(0, 3);
  const mobileMoreItems = allNavItems.filter(
    (item) => !mobilePrimaryItems.some((primary) => primary.path === item.path)
  );
  const currentItem =
    allNavItems.find((item) => item.path === location.pathname) || mainNavItems[0];
  const moreActive = mobileMoreItems.some((item) => item.path === location.pathname);
  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [moreOpen]);

  const toggleTheme = () => setTheme(resolvedTheme === "dark" ? "light" : "dark");
  const handleLogout = () => {
    setMoreOpen(false);
    clearToken();
    navigate("/", { replace: true });
  };

  return (
    <>
      <nav className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/95 text-slate-900 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 dark:text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/upload"
            className="group flex min-w-0 items-center gap-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            aria-label="Cyno Connect home"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-200 transition-transform group-hover:scale-105 dark:shadow-indigo-950/50">
              <Icon name="scan" className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold tracking-tight sm:text-base">
                Cyno Connect
              </span>
              <span className="block truncate text-[10px] font-bold uppercase tracking-[0.14em] text-indigo-600 dark:text-indigo-300 md:text-slate-400 md:dark:text-slate-500">
                <span className="md:hidden">{currentItem.pageTitle}</span>
                <span className="hidden md:inline">Card workspace</span>
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
            {allNavItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={active ? "page" : undefined}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    active
                      ? "bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  }`}
                >
                  <Icon name={item.icon} className="h-[18px] w-[18px]" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <ThemeButton resolvedTheme={resolvedTheme} onClick={toggleTheme} />
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sign out"
              title="Sign out"
              className="hidden h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-rose-900 dark:hover:bg-rose-950/50 dark:hover:text-rose-300 md:grid"
            >
              <Icon name="logout" className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </nav>

      <nav className="mobile-nav-safe fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-2 pt-2 shadow-[0_-8px_30px_-18px_rgba(15,23,42,0.45)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 md:hidden" aria-label="Mobile navigation">
        <div className="mx-auto grid max-w-md grid-cols-4 gap-1">
          {mobilePrimaryItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={active ? "page" : undefined}
                className={`flex min-h-[3.35rem] flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  active
                    ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                    : "text-slate-400 hover:bg-slate-50 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-900 dark:hover:text-slate-200"
                }`}
              >
                <Icon name={item.icon} className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            aria-controls="mobile-more-menu"
            className={`flex min-h-[3.35rem] flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              moreActive || moreOpen
                ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                : "text-slate-400 hover:bg-slate-50 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-900 dark:hover:text-slate-200"
            }`}
          >
            <Icon name="more" className="h-5 w-5" />
            More
          </button>
        </div>
      </nav>

      {moreOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/65 backdrop-blur-sm md:hidden"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setMoreOpen(false);
          }}
        >
          <section
            id="mobile-more-menu"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
            className="mobile-safe-bottom w-full rounded-t-3xl border-t border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <header className="flex items-center justify-between px-1 pb-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
                  Workspace
                </p>
                <h2 id="mobile-more-title" className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                  More options
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label="Close more options"
                className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </header>

            <div className="space-y-2">
              {mobileMoreItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex min-h-14 items-center gap-3 rounded-2xl border border-slate-200 px-4 text-sm font-bold text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50 dark:border-slate-700 dark:text-slate-200 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/40"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                    <Icon name={item.icon} className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1">{item.label}</span>
                  <Icon name="chevron" className="h-4 w-4 text-slate-400" />
                </Link>
              ))}
            </div>

            <div className="mt-5 rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
              <p className="px-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                Appearance
              </p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {THEME_OPTIONS.map((option) => {
                  const selected = theme === option;
                  const icon = option === "system" ? "monitor" : option === "dark" ? "moon" : "sun";
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setTheme(option)}
                      className={`flex min-h-12 items-center justify-center gap-2 rounded-xl text-xs font-bold capitalize transition ${
                        selected
                          ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:text-indigo-300 dark:ring-slate-700"
                          : "text-slate-500 hover:bg-white dark:text-slate-400 dark:hover:bg-slate-800"
                      }`}
                    >
                      <Icon name={icon} className="h-4 w-4" />
                      {option}
                      {selected && <Icon name="check" className="h-3.5 w-3.5" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                handleLogout();
              }}
              className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-rose-200 text-sm font-bold text-rose-600 transition hover:bg-rose-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950/40"
            >
              <Icon name="logout" className="h-4 w-4" /> Sign out
            </button>
          </section>
        </div>
      )}
    </>
  );
};

const App = () => {
  const { token, role, isInternal } = useAuthStore();
  const setIsInternal = useAuthStore((state) => state.setIsInternal);
  const { theme } = useThemeStore();
  const [systemTheme, setSystemTheme] = useState(getSystemTheme());
  const location = useLocation();
  const navigate = useNavigate();
  const showNavBar = Boolean(token) && location.pathname !== "/";
  const resolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    if (!token && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate, token]);

  useEffect(() => {
    if (process.env.REACT_APP_BACKEND_URL === "development") {
      setIsInternal(true);
      return;
    }
    const host = window.location.hostname;
    if (host === "connect.cyno.co.in") {
      setIsInternal(true);
    } else if (host === "app.cyno.co.in") {
      setIsInternal(false);
    } else {
      setIsInternal(true);
    }
  }, [setIsInternal]);

  useEffect(() => {
    applyTheme(theme);
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mediaQuery) {
      setSystemTheme("light");
      return undefined;
    }
    const updateSystemTheme = (event) => {
      setSystemTheme(event.matches ? "dark" : "light");
      if (theme === "system") applyTheme("system");
    };
    setSystemTheme(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener?.("change", updateSystemTheme);
    return () => mediaQuery.removeEventListener?.("change", updateSystemTheme);
  }, [theme]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      {showNavBar && <NavBar resolvedTheme={resolvedTheme} />}

      <div className={`min-w-0 flex-grow ${showNavBar ? "pb-24 md:pb-0" : ""}`}>
        <Routes>
          <Route path="/" element={isInternal ? <LoginPage /> : <GoogleLogin />} />
          <Route path="/upload" element={<PhotoUploadPage />} />
          <Route path="/cards" element={<CardsPage />} />
          {role === "admin" && <Route path="/users" element={<Users />} />}
          <Route path="/edit-template" element={<EditTemplatePage />} />
          <Route path="/campaign-profiles" element={<CampaignProfilesPage />} />
        </Routes>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        theme={resolvedTheme}
      />
    </div>
  );
};

const RootApp = () => (
  <Router>
    <App />
  </Router>
);

export default RootApp;
